import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	viewChild,
} from '@angular/core';
import {
	FormControl,
	FormGroupDirective,
	NgForm,
	UntypedFormControl,
	UntypedFormGroup,
	ValidationErrors,
} from '@angular/forms';
import { isEmpty, isNil } from 'lodash-es';
import { Moment } from 'moment';
import { Observer, Subscription } from 'rxjs';
import { addAriaAttributesToMatSelect, Selectable, SelectableSpaltenFeld, SpaltenFeld } from '../schir-utils';
import { ErrorStateMatcher } from '@angular/material/core';
import { formatAsDatepicker } from '../../../../../apps/int-client-e2e/src/helpers/date';
import { MatCheckbox } from '@angular/material/checkbox';

/** Error when invalid control is dirty, touched, submitted or empty. */
export class ShowOnDirtyAndEmptyErrorStateMatcher implements ErrorStateMatcher {
	public isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
		const isSubmitted = form && form.submitted;
		return !!(
			control &&
			control.invalid &&
			(control.value == '' || control.dirty || control.touched || isSubmitted)
		);
	}
}

@Component({
	selector: 'schir-int-client-one-value-editor',
	templateUrl: './one-value-editor.component.html',
	styleUrls: ['./one-value-editor.component.scss'],
	providers: [
		{
			provide: ErrorStateMatcher,
			useClass: ShowOnDirtyAndEmptyErrorStateMatcher,
		},
	],
	standalone: false,
})
export class OneValueEditorComponent implements OnInit, AfterViewInit, OnChanges {
	@Input() public formGroup: UntypedFormGroup;
	@Input() public column; // RegisterBlattColumn;
	@Input('type') public inputType: string;
	@Input('values') public selectableValues: Selectable[];
	@Input() public defaultSelectable: Selectable;

	//Neue Struktur
	@Input('fieldControl') public inputFieldControl: UntypedFormControl;

	@Input('label') public inputLabel: string;
	@Input() public feld: SpaltenFeld;
	@Input() public readonly: boolean = false;
	@Input() public required: boolean = false;
	@Input() public stepsize: number = 0.01;
	@Input() public initialFocus: boolean;
	@Input() public evaluateAfterChange: boolean = false;
	@Input() public hideBoxShadow: boolean = false;

	@Input() public allowEmptyString: boolean = true;

	@Output() public value: EventEmitter<string | number | boolean | Moment | Date> = new EventEmitter();

	private readonly field = viewChild<ElementRef>('field');

	protected values: Selectable[];
	protected defaultValue: string;
	protected messages = ValidationMessages;
	protected subscription: Subscription = null;
	protected readonly matcher: ErrorStateMatcher = new (class implements ErrorStateMatcher {
		public isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
			return Boolean(control && control.invalid && (control.dirty || control.touched));
		}
	})();

	private resetToDefaultValueObserver: Observer<any> = new (class implements Observer<any> {
		constructor(private parent: OneValueEditorComponent) {
			// nothing to do
		}

		public next(value: any): void {
			const control = this.parent.fieldControl;
			const defaultValue = this.parent.defaultSelectable;

			if (defaultValue && !value && control && !control.value && control.pristine) {
				const emit = this.parent.feld?.type == 'select';
				if (this.parent.isTextType) {
					control.patchValue(defaultValue, { onlySelf: true, emitEvent: emit });
				} else {
					control.patchValue(defaultValue.value, { onlySelf: true, emitEvent: emit });
				}
			}
		}

		public complete(): void {}

		public error(_err: any): void {}
	})(this);

	public ngOnInit(): void {
		addAriaAttributesToMatSelect();

		if (this.feld?.validators != null) {
			this.fieldControl.setValidators(this.feld.validators);
		}
	}

	public ngAfterViewInit(): void {
		// Achtung! Jeder der beiden Werte kann bei numerischen Feldern undefiniert sein, mindestens einer ist aber 'number'
		const field = this.field();
		if (field && (this.feld?.type === 'number' || this.inputType === 'number')) {
			// Je nach Browser kann die Komponente auch Buchstaben annehmen, die führen beim Speichern zu Fehlern. Daher hier eine Whitelist.
			const numberChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
			const commaChar = [','];
			const validChars = [...numberChars, ...commaChar];

			field.nativeElement.addEventListener('keydown', (e: KeyboardEvent) => {
				const key = e.key;
				if (key === 'ArrowUp' || key === 'ArrowDown') {
					e.preventDefault();
					return;
				}
				if (key.length == 1 && !validChars.includes(key)) {
					e.preventDefault();
				}
			});

			// Mausrad soll den Wert bei Betragsfeldern ebenfalls nicht verändern
			field.nativeElement.addEventListener(
				'wheel',
				(e: WheelEvent) => {
					if (document.activeElement === field.nativeElement) {
						e.preventDefault();
					}
				},
				{ passive: false }
			);
		}
	}

	/**
	 * Mit dem MDC-Checkboxen bewirkt event.preventDefault(), stopImmediatePropagation() usw. nur ein Freeze der
	 * Ansicht. Um Änderung des Datenmodells vermeiden, wird einfach ein anderes FieldControl angegeben. Einfach das
	 * Value zu übergeben, verhindert zwar ebenfalls das Editieren, führt aber zu einer falschen Anzeige.
	 */
	protected get fakeFieldControl(): UntypedFormControl {
		const control = this.fieldControl;
		const dummy = new UntypedFormControl();
		dummy.setValue(control?.getRawValue());
		return dummy;
	}

	public get fieldControl() {
		if (!isNil(this.inputFieldControl)) {
			return this.inputFieldControl;
		}
		if (isNil(this.formGroup)) {
			console.warn('Missing Input formGroup');
			return null;
		}
		if (isNil(this.formGroup.controls)) {
			return this.formGroup;
		} // manchmal wird das Element anstelle der Gruppe gegeben

		if (isNil(this.feld)) {
			console.warn('Missing Input column (feld)');
			return null;
		}

		const control = this.formGroup.controls[this.feld.name];
		if (isNil(control)) {
			console.warn('missing form field control for column field ' + this.feld.name + ' in form Group');
			return null;
		}
		return control;
	}

	public get errorMessages(): string | ValidationErrors {
		switch (this.type) {
			case 'text':
				if (this.fieldControl?.hasError('uSigTooLong')) {
					return this.messages.USIG_TOO_LONG;
				}
				if (this.fieldControl?.hasError('nonBlank')) {
					return this.messages.NON_BLANK;
				}
				if (this.fieldControl?.hasError('required')) {
					return this.messages.NO_VALID_VALUE;
				}
				if (this.fieldControl?.hasError('maxlength')) {
					const error = this.fieldControl?.getError('maxlength');

					return 'Der eingegebene Wert darf maximal ' + error.requiredLength + ' Zeichen lang sein.';
				}
				break;
			case 'select':
				if (this.fieldControl?.hasError('required')) {
					return this.label + ' ' + this.messages.MISSING_REQUIRED_SELECT_VALUE;
				}
				break;
			case 'number':
				if (this.fieldControl?.hasError('required')) {
					return this.label + ' ' + this.messages.MISSING_REQUIRED_SELECT_VALUE;
				} else if (this.fieldControl?.hasError('pattern')) {
					return this.messages.WRONG_DECIMAL_NUMBER;
				} else if (this.fieldControl?.hasError('wrongBlattnummer')) {
					return this.messages.WRONG_BLATTNUMMER;
				}
				break;
			default:
				return isNil(this.formGroup) ? this.fieldControl.errors : this.formGroup.errors;
		}
	}

	private get type(): string {
		if (!isNil(this.inputType)) {
			return this.inputType;
		}
		if (!isNil(this.feld)) {
			return this.feld.type;
		}
		return 'text';
	}

	protected clearField(formControl: UntypedFormControl, event: Event): void {
		event.preventDefault();
		formControl.setValue(null);
		formControl.markAsDirty();
	}

	protected get label(): string {
		return !isNil(this.inputLabel) ? this.inputLabel : this.feld?.label;
	}

	protected get isTextType(): boolean {
		return this.type === 'text';
	}

	protected get isNumberType(): boolean {
		return this.type === 'number';
	}

	protected get isSelectType(): boolean {
		return this.type === 'select';
	}

	protected get isAutocompleteType(): boolean {
		return this.type === 'autocomplete';
	}

	protected get isDateType(): boolean {
		return this.type === 'date';
	}

	protected get isLongTextType(): boolean {
		return this.type === 'longText';
	}

	protected get isCheckboxType(): boolean {
		return this.type === 'checkbox';
	}

	protected onChange(value: string | number | boolean | Moment): void {
		if (!this.isCheckboxType) {
			if (!this.allowEmptyString && isEmpty(value)) {
				value = null;

				this.fieldControl.setValue(null);
			}
		}

		this.value.emit(value);
	}

	public ngOnChanges(_changes: SimpleChanges): void {
		this.values = this.getValues();

		// if the defaultSelectable was given with a label only, find the corresponding value matching by label
		if (this.defaultSelectable && !this.defaultSelectable.value) {
			const found = this.values?.find(value => value.label == this.defaultSelectable.label);

			if (found) {
				this.defaultSelectable = found;
			}
		}

		if (this.subscription) {
			this.subscription.unsubscribe();
		}

		if (this.defaultSelectable && this.fieldControl) {
			this.subscription = this.fieldControl.valueChanges.subscribe(this.resetToDefaultValueObserver);

			// trigger the observer to set the default value initially if applicable
			this.resetToDefaultValueObserver.next(null);
		}

		if (this.defaultSelectable && this.formGroup && !this.fieldControl.value) {
			this.fieldControl.setValue(this.feld.defaultValue.value);
			this.fieldControl.markAsDirty();
		}
	}

	private getValues(): Selectable[] {
		if (!isNil(this.selectableValues)) {
			return this.selectableValues;
		}
		if (isNil(this.feld)) {
			return [];
		}
		const feldValues = (<SelectableSpaltenFeld>this.feld).values;

		return isNil(feldValues)
			? []
			: feldValues.map(value => {
					return <Selectable>{ label: value, value };
				});
	}

	protected onlyGainFocus(event: MouseEvent, element: MatCheckbox): void {
		element.focus();
		event.preventDefault();
	}

	protected getDisplayValue(): any {
		const currentValue = this.fieldControl.value;
		switch (this.type) {
			case 'date':
				try {
					return formatAsDatepicker(currentValue);
				} catch (_e) {
					console.log('cannot format date', currentValue);
					return currentValue;
				}
			case 'select':
				return this.values?.find(v => v.value === currentValue)?.label;
			default:
				return currentValue;
		}
	}

	protected get initialFocusString(): string {
		return String(this.initialFocus);
	}
}

export enum ValidationMessages {
	MISSING_REQUIRED_SELECT_VALUE = 'Ein Wert muss ausgewählt werden.',
	WRONG_DECIMAL_NUMBER = 'Es sind maximal zwei Nachkommastellen zulässig.',
	NO_VALID_VALUE = 'Bitte geben sie einen gültigen Wert ein.',
	NON_BLANK = 'Der Wert darf nicht leer sein.',
	USIG_TOO_LONG = 'Es sind maximal 8 Zeichen zulässig.',
	WRONG_BLATTNUMMER = 'Maximal 8 Ziffern (ohne führende Nullen) sind zulässig.',
}
